import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { Link as TSLink } from '@tanstack/react-router';

// Drop-in replacements for react-router-dom's Link/NavLink, kept so the app can
// link with a plain string `to` (template strings like `/api/${id}`). They fall
// back to a real <a> for external/protocol URLs, and reproduce NavLink's
// `className`-as-function active API on top of TanStack's activeProps/inactiveProps.

const isExternal = (to: string) => /^(https?:|mailto:|tel:|#)/.test(to);

type Rest = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;

export type LinkProps = Rest & { to: string; children?: ReactNode };

export const Link = ({ to, children, ...rest }: LinkProps) => {
  if (isExternal(to)) {
    return (
      <a href={to} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <TSLink to={to as never} {...(rest as object)}>
      {children}
    </TSLink>
  );
};

type ClassFn = (state: { isActive: boolean }) => string;

export type NavLinkProps = Omit<Rest, 'className'> & {
  to: string;
  /** exact-match active (react-router's `end`) */
  end?: boolean;
  className?: string | ClassFn;
  children?: ReactNode;
};

export const NavLink = ({ to, end, className, children, ...rest }: NavLinkProps) => {
  const activeClass = typeof className === 'function' ? className({ isActive: true }) : className;
  const inactiveClass =
    typeof className === 'function' ? className({ isActive: false }) : className;

  if (isExternal(to)) {
    return (
      <a href={to} className={inactiveClass} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <TSLink
      to={to as never}
      activeOptions={{ exact: end ?? false }}
      activeProps={{ className: activeClass }}
      inactiveProps={{ className: inactiveClass }}
      {...(rest as object)}
    >
      {children}
    </TSLink>
  );
};
